/**
 * PidDiffPanel — Wave CG.2
 *
 * Compares two P&ID revisions and shows what changed: added / removed /
 * moved equipment, rerouted streams, added / removed / reparented
 * instruments, and valve-count delta. Sub-sector & country agnostic —
 * the backend `pid_diff_service` parses the renderer's data-attributes.
 *
 * Backend endpoints:
 *   GET /api/projects/:pid/pid/sheet/:sheet_id/revisions
 *   GET /api/projects/:pid/pid/sheet/:sheet_id/diff?from_rev=&to_rev=
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatErr } from "../../lib/api";
import {
   AlertCircle, ChevronDown, GitCompareArrows, Loader2,
   ArrowUpRight, ArrowDownRight, ArrowRightLeft,
} from "lucide-react";

const COL = {
   surface: "var(--pf-surface, #0f1729)",
   surface2: "var(--pf-surface-2, #1a2332)",
   border: "var(--pf-border, #1e293b)",
   text: "var(--pf-text, #e2e8f0)",
   dim: "var(--pf-text-muted, #94a3b8)",
   spark: "var(--pf-spark, #22d3ee)",
   green: "#22c55e",
   amber: "#f59e0b",
   red: "#ef4444",
   blue: "#0F62FE",
};
const mono = { fontFamily: "'JetBrains Mono', 'Fira Code', monospace" };

export default function PidDiffPanel({ projectId, sheetId, sheetTitle }) {
   const [revisions, setRevisions] = useState(null);
   const [fromRev, setFromRev] = useState(null);
   const [toRev, setToRev] = useState(null);
   const [diff, setDiff] = useState(null);
   const [loading, setLoading] = useState(false);
   const [err, setErr] = useState("");

   // ── Load revision history ──
   const loadRevisions = useCallback(async () => {
      if (!projectId || !sheetId) return;
      try {
         setErr("");
         const r = await api.get(
            `/projects/${projectId}/pid/sheet/${sheetId}/revisions`);
         setRevisions(r.data);
         // Default selection: previous revision → current
         const cur = r.data?.current_revision || 0;
         const history = (r.data?.history || []).filter(
            (h) => h.revision !== cur);
         setToRev(cur);
         setFromRev(history[0]?.revision ?? null);
      } catch (e) {
         setErr(formatErr(e));
      }
   }, [projectId, sheetId]);

   useEffect(() => { loadRevisions(); }, [loadRevisions]);

   // ── Load diff ──
   const loadDiff = useCallback(async () => {
      if (!projectId || !sheetId) return;
      setLoading(true); setErr("");
      try {
         const params = new URLSearchParams();
         if (fromRev != null) params.set("from_rev", String(fromRev));
         if (toRev != null) params.set("to_rev", String(toRev));
         const r = await api.get(
            `/projects/${projectId}/pid/sheet/${sheetId}/diff?${params}`);
         setDiff(r.data);
      } catch (e) {
         setErr(formatErr(e));
         setDiff(null);
      } finally {
         setLoading(false);
      }
   }, [projectId, sheetId, fromRev, toRev]);

   useEffect(() => {
      if (revisions) loadDiff();
   }, [revisions, fromRev, toRev, loadDiff]);

   const revOptions = useMemo(() => {
      const cur = revisions?.current_revision;
      const hist = revisions?.history || [];
      const opts = [];
      if (cur != null) opts.push({ rev: cur, label: `r${cur} (current)` });
      for (const h of hist) {
         if (h.revision !== cur) {
            opts.push({ rev: h.revision, label: `r${h.revision}` });
         }
      }
      return opts;
   }, [revisions]);

   if (!sheetId) {
      return (
         <div data-testid="pid-diff-empty"
               style={{ padding: 24, color: COL.dim, ...mono, fontSize: 12 }}>
            Open a P&amp;ID sheet to see its revision diff.
         </div>
      );
   }

   const s = diff?.summary || {};
   const hasChanges = (s.total_changes || 0) > 0;
   const noHistory = diff?.no_history;

   return (
      <div data-testid="pid-diff-panel"
            style={{ ...mono, color: COL.text, fontSize: 12,
                        height: "100%", overflow: "auto",
                        padding: "16px 20px" }}>
         {/* Header + revision picker */}
         <div style={{ display: "flex", alignItems: "center", gap: 12,
                          flexWrap: "wrap", marginBottom: 16 }}>
            <GitCompareArrows size={16} style={{ color: COL.spark }}/>
            <h3 data-testid="pid-diff-title"
                  style={{ margin: 0, fontSize: 13, fontWeight: 700,
                              letterSpacing: 0.6 }}>
               REVISION DIFF · {sheetId}
            </h3>
            <div style={{ marginLeft: "auto", display: "flex", gap: 6,
                              alignItems: "center" }}>
               <span style={{ color: COL.dim, fontSize: 11 }}>FROM</span>
               <select data-testid="pid-diff-from"
                       value={fromRev ?? ""}
                       onChange={(e) => setFromRev(
                          e.target.value === "" ? null : Number(e.target.value))}
                       style={selStyle()}>
                  <option value="">— previous —</option>
                  {revOptions.map((o) => (
                     <option key={o.rev} value={o.rev}>{o.label}</option>
                  ))}
               </select>
               <ChevronDown size={12} style={{ color: COL.dim }}/>
               <span style={{ color: COL.dim, fontSize: 11 }}>TO</span>
               <select data-testid="pid-diff-to"
                       value={toRev ?? ""}
                       onChange={(e) => setToRev(
                          e.target.value === "" ? null : Number(e.target.value))}
                       style={selStyle()}>
                  {revOptions.map((o) => (
                     <option key={o.rev} value={o.rev}>{o.label}</option>
                  ))}
               </select>
            </div>
         </div>

         {sheetTitle && (
            <div style={{ color: COL.dim, fontSize: 11, marginBottom: 14 }}>
               {sheetTitle}
            </div>
         )}

         {err && (
            <div data-testid="pid-diff-error"
                  style={{ background: "#7f1d1d", color: "#fee2e2",
                              padding: 10, borderRadius: 4, marginBottom: 14 }}>
               <AlertCircle size={12} style={{ display: "inline", marginRight: 6 }}/>
               {err}
            </div>
         )}

         {loading && (
            <div style={{ color: COL.dim, fontSize: 11, marginBottom: 14 }}>
               <Loader2 size={12}
                  style={{ display: "inline", marginRight: 6,
                              animation: "spin 1s linear infinite" }}/>
               Comparing revisions…
            </div>
         )}

         {!loading && noHistory && (
            <div data-testid="pid-diff-no-history"
                  style={{ background: COL.surface2, padding: 14,
                              borderRadius: 4, color: COL.dim,
                              border: `1px dashed ${COL.border}` }}>
               This sheet has no prior revisions yet. Generate the P&amp;ID
               again — the existing version will be archived and the next
               compare will show what changed.
            </div>
         )}

         {!loading && diff && !noHistory && (
            <>
               {/* Summary stats */}
               <div data-testid="pid-diff-summary"
                     style={{ display: "grid", gap: 8,
                                 gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                 marginBottom: 18 }}>
                  <StatCard label="TOTAL CHANGES"
                         value={s.total_changes}
                         color={hasChanges ? COL.spark : COL.dim}
                         testid="stat-total"/>
                  <StatCard label="EQUIPMENT  ±"
                         value={`+${s.equipment_added}  /  −${s.equipment_removed}  /  ↔${s.equipment_moved}`}
                         color={COL.green}
                         testid="stat-equipment"/>
                  <StatCard label="STREAMS  ±"
                         value={`+${s.streams_added}  /  −${s.streams_removed}  /  ⤳${s.streams_rerouted}`}
                         color={COL.amber}
                         testid="stat-streams"/>
                  <StatCard label="INSTRUMENTS  ±"
                         value={`+${s.instruments_added}  /  −${s.instruments_removed}  /  ⟲${s.instruments_reparented}`}
                         color={COL.blue}
                         testid="stat-instruments"/>
                  <StatCard label="VALVES Δ"
                         value={(s.valves_delta || 0) >= 0
                            ? `+${s.valves_delta}` : `${s.valves_delta}`}
                         color={(s.valves_delta || 0) === 0 ? COL.dim : COL.spark}
                         testid="stat-valves"/>
               </div>

               {/* Detail tables */}
               <DetailGroup title="Equipment added"
                  icon={<ArrowUpRight size={12} style={{ color: COL.green }}/>}
                  items={diff.equipment?.added || []}
                  columns={["tag"]} testid="diff-eq-added"/>
               <DetailGroup title="Equipment removed"
                  icon={<ArrowDownRight size={12} style={{ color: COL.red }}/>}
                  items={diff.equipment?.removed || []}
                  columns={["tag"]} testid="diff-eq-removed"/>
               <DetailGroup title="Equipment moved"
                  icon={<ArrowRightLeft size={12} style={{ color: COL.blue }}/>}
                  items={diff.equipment?.moved || []}
                  columns={["tag", "dx", "dy"]} testid="diff-eq-moved"/>
               <DetailGroup title="Streams rerouted"
                  icon={<ArrowRightLeft size={12} style={{ color: COL.amber }}/>}
                  items={diff.streams?.rerouted || []}
                  columns={["id", "line_no"]} testid="diff-st-rerouted"/>
               <DetailGroup title="Streams added"
                  icon={<ArrowUpRight size={12} style={{ color: COL.green }}/>}
                  items={diff.streams?.added || []}
                  columns={["id", "line_no"]} testid="diff-st-added"/>
               <DetailGroup title="Streams removed"
                  icon={<ArrowDownRight size={12} style={{ color: COL.red }}/>}
                  items={diff.streams?.removed || []}
                  columns={["id", "line_no"]} testid="diff-st-removed"/>
               <DetailGroup title="Instruments added"
                  icon={<ArrowUpRight size={12} style={{ color: COL.green }}/>}
                  items={diff.instruments?.added || []}
                  columns={["tag", "parent", "type"]} testid="diff-inst-added"/>
               <DetailGroup title="Instruments removed"
                  icon={<ArrowDownRight size={12} style={{ color: COL.red }}/>}
                  items={diff.instruments?.removed || []}
                  columns={["tag", "parent", "type"]} testid="diff-inst-removed"/>
               <DetailGroup title="Instruments reparented"
                  icon={<ArrowRightLeft size={12} style={{ color: COL.blue }}/>}
                  items={diff.instruments?.reparented || []}
                  columns={["tag", "before_parent", "after_parent"]}
                  testid="diff-inst-reparented"/>

               {!hasChanges && (
                  <div data-testid="pid-diff-no-changes"
                        style={{ color: COL.dim, padding: 18,
                                    textAlign: "center", fontStyle: "italic" }}>
                     No differences detected between r{diff.from_revision} →
                     r{diff.to_revision}.
                  </div>
               )}
            </>
         )}
      </div>
   );
}

function selStyle() {
   return {
      background: COL.surface2, color: COL.text,
      border: `1px solid ${COL.border}`, borderRadius: 3,
      padding: "4px 8px", fontSize: 11,
      fontFamily: "'JetBrains Mono', monospace",
   };
}

function StatCard({ label, value, color, testid }) {
   return (
      <div data-testid={testid}
            style={{ background: COL.surface2,
                        border: `1px solid ${COL.border}`,
                        padding: "10px 12px", borderRadius: 4 }}>
         <div style={{ fontSize: 9, color: COL.dim, letterSpacing: 0.6 }}>
            {label}
         </div>
         <div style={{ fontSize: 13, fontWeight: 700, color,
                          marginTop: 4 }}>
            {value}
         </div>
      </div>
   );
}

function DetailGroup({ title, icon, items, columns, testid }) {
   if (!items?.length) return null;
   return (
      <div data-testid={testid}
            style={{ marginBottom: 14,
                        border: `1px solid ${COL.border}`,
                        borderRadius: 4, overflow: "hidden" }}>
         <div style={{ background: COL.surface2,
                          padding: "8px 12px",
                          display: "flex", alignItems: "center", gap: 8,
                          fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>
            {icon}
            <span>{title}</span>
            <span style={{ marginLeft: "auto", color: COL.dim,
                              fontSize: 10 }}>
               {items.length}
            </span>
         </div>
         <table style={{ width: "100%", borderCollapse: "collapse",
                            fontSize: 11 }}>
            <thead>
               <tr style={{ background: COL.surface,
                                color: COL.dim, fontSize: 10 }}>
                  {columns.map((c) => (
                     <th key={c} style={{ padding: "6px 12px",
                                              textAlign: "left",
                                              textTransform: "uppercase",
                                              letterSpacing: 0.4 }}>
                        {c.replace(/_/g, " ")}
                     </th>
                  ))}
               </tr>
            </thead>
            <tbody>
               {items.map((it, i) => (
                  <tr key={i} data-testid={`${testid}-row-${i}`}
                         style={{ borderTop: `1px solid ${COL.border}` }}>
                     {columns.map((c) => (
                        <td key={c} style={{ padding: "6px 12px",
                                                  color: COL.text }}>
                           {String(it[c] ?? "—")}
                        </td>
                     ))}
                  </tr>
               ))}
            </tbody>
         </table>
      </div>
   );
}
